import { format } from "date-fns";

import { faCircleCheck, faUser } from "@fortawesome/pro-regular-svg-icons";
import { faSparkles } from "@fortawesome/pro-solid-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { TableColumn } from "@/components/Table/Table";

import { ContactBlacklistedProspect, OrganizationInfo } from "../../Contacts/schema/contacts";
import { competitorDecidedByName } from "./competitorAttribution";

/**
 * The per-row way out of the Competitors tab. Lives in its own module rather than
 * in `allColumns.tsx`, which is far over the length ratchet and may only shrink.
 */
export const renderOrgUnflagCompetitorColumn = (
	ts: (key: string) => string,
	onUnflag: (organizationId: number, companyName?: string) => void
): TableColumn => ({
	title: ts("competitors.unflagColumn"),
	width: "fit-content",
	minWidth: "180px",
	uid: "orgUnflagCompetitor",
	hideable: true,
	moveable: true,
	show: true,
	render: (row: OrganizationInfo) => (
		<Button
			color={ButtonColor.ACTION_SECONDARY}
			image={<Icon className="w-3 mr-1" icon={faCircleCheck} />}
			size={ButtonSize.XS}
			title={ts("competitors.unflagAction")}
			type="button"
			onClick={(e) => {
				e.stopPropagation();
				onUnflag(row.id, row.name ?? undefined);
			}}
		/>
	),
});

/**
 * The same way out, offered on the Blacklist tab.
 *
 * Removing a COMPETITOR row with the tab's own "Remove from Blacklist" action
 * does not stick: the company keeps its Competitor score result, so the next
 * scoring run puts it straight back. This action runs the real un-flag instead.
 */
export const renderBlacklistUnflagCompetitorColumn = (
	ts: (key: string) => string,
	onUnflag: (organizationId: number, companyName?: string) => void
): TableColumn => ({
	title: ts("competitors.unflagColumn"),
	width: "fit-content",
	minWidth: "180px",
	uid: "blacklistUnflagCompetitor",
	hideable: true,
	moveable: true,
	show: true,
	render: (row: ContactBlacklistedProspect) => {
		const organizationId = row.organization?.id;

		if (row.blacklistOrigin !== "COMPETITOR" || !organizationId) {
			return <span className="text-sm text-text-meta">-</span>;
		}

		return (
			<Button
				color={ButtonColor.ACTION_SECONDARY}
				image={<Icon className="w-3 mr-1" icon={faCircleCheck} />}
				size={ButtonSize.XS}
				title={ts("competitors.unflagAction")}
				type="button"
				onClick={(e) => {
					e.stopPropagation();
					onUnflag(organizationId, row.organization?.name ?? undefined);
				}}
			/>
		);
	},
});

/** AI-detected vs a hand-made call — `manuallyAdded` on the score result is what says which. */
export const renderCompetitorDetectedByColumn = (ts: (key: string) => string): TableColumn => ({
	title: ts("blacklist.detectedBy"),
	width: "fit-content",
	minWidth: "150px",
	uid: "competitorDetectedBy",
	hideable: true,
	moveable: true,
	show: true,
	render: (row: OrganizationInfo) => (
		<span className="inline-flex items-center gap-1.5 text-sm text-text">
			<Icon
				className="text-text-meta"
				icon={row.scoreCompanyCategoryResult?.manuallyAdded ? faUser : faSparkles}
			/>
			{ts(
				row.scoreCompanyCategoryResult?.manuallyAdded
					? "blacklist.detectedByManual"
					: "blacklist.detectedByAi"
			)}
		</span>
	),
});

/**
 * Who made the call. Only a manual verdict has someone to name, and only since PREC-3594 —
 * earlier manual changes were never attributed, so they read as AI-detected.
 */
export const renderCompetitorAddedByColumn = (ts: (key: string) => string): TableColumn => ({
	title: ts("blacklist.addedBy"),
	width: "fit-content",
	minWidth: "160px",
	uid: "competitorAddedBy",
	hideable: true,
	moveable: true,
	show: true,
	render: (row: OrganizationInfo) => {
		const name = competitorDecidedByName(row);

		return name ? (
			<span className="text-sm text-text">{name}</span>
		) : (
			<span className="text-sm text-text-meta">-</span>
		);
	},
});

/** When the company became a competitor. */
export const renderCompetitorDetectedAtColumn = (ts: (key: string) => string): TableColumn => ({
	title: ts("competitors.detectedAtColumn"),
	width: "fit-content",
	minWidth: "160px",
	uid: "competitorDetectedAt",
	hideable: true,
	moveable: true,
	show: true,
	render: (row: OrganizationInfo) => {
		const detectedAt = row.scoreCompanyCategoryResult?.createdAt;

		if (!detectedAt) {
			return <span className="text-sm text-text-meta">-</span>;
		}

		return (
			<span className="text-sm text-text">{format(new Date(detectedAt), "HH:mm dd.MM.yyyy")}</span>
		);
	},
});
